import {
    createContext,
    memo,
    PropsWithChildren,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useLocation } from "react-router-dom";

import { Routes as AppRoutes } from "@/constants/routes";
import { isNyscPathOrDomain } from "@/utils/isNyscFlow";

import { useGender } from "./GenderProvider";

export const enum Themes {
    Male = "male",
    Female = "female",
}

export const enum AdditionalThemes {
    LightMale = "light-male",
}

export interface ThemeContextType {
    theme: Themes;
    setTheme: (theme: Themes) => void;
    isLightTheme: boolean;
    isLightMaleTheme: boolean;
}

export const ThemeContext = createContext<ThemeContextType>({
    setTheme: () => null,
    theme: Themes.Male,
    isLightTheme: false,
    isLightMaleTheme: false,
});

const ThemeProvider = ({ children }: PropsWithChildren) => {
    const { isFemale } = useGender();
    const { pathname } = useLocation();

    const isFunnelFox = useMemo(() => pathname.includes(AppRoutes.FFPaywall), [pathname]);
    const isPromoCodeFlow = useMemo(
        () => pathname.includes(AppRoutes.RedeemCode) || pathname.includes(AppRoutes.RedeemPSGCode),
        [pathname]
    );
    const isNyscFlow = useMemo(() => isNyscPathOrDomain(pathname), [pathname]);
    const isPsgAuthFlow = useMemo(
        () => pathname === `/${AppRoutes.PsgBase}` || pathname.startsWith(`/${AppRoutes.PsgBase}/`),
        [pathname]
    );
    const isJune26GiveawayFlow = useMemo(
        () =>
            pathname === `/${AppRoutes.June26Giveaway}` ||
            pathname.startsWith(`/${AppRoutes.June26Giveaway}/`),
        [pathname]
    );

    /** Abandon-cart paywalls are light UI; they are not under redeem/nysc/psg paths. */
    const isAbandonCartPaywallRoute = useMemo(() => {
        if (pathname.includes(`/${AppRoutes.PromoPaywallBodyScan}`)) {
            return true;
        }
        return (
            pathname === `/${AppRoutes.PromoPaywall}` ||
            pathname.endsWith(`/${AppRoutes.PromoPaywall}`)
        );
    }, [pathname]);

    // Initialize theme with the correct value from the start to avoid color flashing
    const themeValue = useMemo(() => {
        if (
            isFemale ||
            isFunnelFox ||
            isPromoCodeFlow ||
            isNyscFlow ||
            isPsgAuthFlow ||
            isJune26GiveawayFlow ||
            isAbandonCartPaywallRoute
        ) {
            return Themes.Female;
        }
        return Themes.Male;
    }, [
        isFemale,
        isFunnelFox,
        isPromoCodeFlow,
        isNyscFlow,
        isPsgAuthFlow,
        isJune26GiveawayFlow,
        isAbandonCartPaywallRoute,
    ]);

    const [theme, setTheme] = useState<Themes>(themeValue);

    // Update theme state when theme value changes
    useEffect(() => {
        setTheme(themeValue);
    }, [themeValue]);

    useEffect(() => {
        document.body.classList.remove(theme === Themes.Male ? Themes.Female : Themes.Male);
        document.body.classList.add(theme);

        if (isFunnelFox) {
            if (isFemale) {
                document.body.classList.remove(AdditionalThemes.LightMale);
            } else {
                document.body.classList.add(AdditionalThemes.LightMale);
            }
        }
    }, [theme, isFunnelFox, isFemale]);

    const isLightTheme = useMemo(() => theme === Themes.Female, [theme]);
    const isLightMaleTheme = useMemo(
        () => theme === Themes.Female && !isFemale && isFunnelFox,
        [theme, isFemale, isFunnelFox]
    );

    return (
        <ThemeContext.Provider
            value={{
                setTheme,
                theme,
                isLightTheme,
                isLightMaleTheme,
            }}
        >
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    return useContext(ThemeContext);
};

export default memo(ThemeProvider);
